/**
 * PhoneEnrollmentModal Component
 *
 * Modal dialog for adding phone authentication method.
 * Allows users to enroll a phone number for MFA.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { PhoneInput } from 'react-international-phone';
import { parsePhoneNumber } from 'libphonenumber-js';
import 'react-international-phone/style.css';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  phoneInput: {
    '& .react-international-phone-input-container': {
      width: '100%',
    },
    '& .react-international-phone-input': {
      width: '100%',
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      padding: tokens.spacingVerticalS,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      '&:focus': {
        outline: `2px solid ${tokens.colorBrandStroke1}`,
        outlineOffset: '2px',
      },
    },
    '& .react-international-phone-country-selector-button': {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      marginRight: tokens.spacingHorizontalS,
    },
  },
});

interface PhoneEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnroll: (phoneNumber: string, phoneType: string) => Promise<void>;
}

export const PhoneEnrollmentModal: React.FC<PhoneEnrollmentModalProps> = ({
  isOpen,
  onClose,
  onEnroll,
}) => {
  const styles = useStyles();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneType] = useState('mobile'); // Default to mobile, can be extended later
  const [validationError, setValidationError] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhoneNumber('');
      setValidationError('');
      setIsEnrolling(false);
    }
  }, [isOpen]);

  // Phone number validation using libphonenumber-js
  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone || phone.trim() === '') {
      setValidationError('Phone number is required');
      return false;
    }

    try {
      // Parse and validate phone number
      const parsedPhone = parsePhoneNumber(phone);

      if (!parsedPhone) {
        setValidationError('Please enter a valid phone number');
        return false;
      }

      if (!parsedPhone.isValid()) {
        setValidationError('Please enter a valid phone number');
        return false;
      }

      // Ensure E.164 format
      const e164Format = parsedPhone.format('E.164');
      if (!e164Format || !e164Format.startsWith('+')) {
        setValidationError('Phone number must be in international format');
        return false;
      }

      setValidationError('');
      return true;
    } catch (error) {
      setValidationError('Please enter a valid phone number');
      return false;
    }
  };

  const handleSubmit = async () => {
    // Validate phone number
    if (!validatePhoneNumber(phoneNumber)) {
      return;
    }

    try {
      setIsEnrolling(true);

      // Parse phone number to get E.164 format
      const parsedPhone = parsePhoneNumber(phoneNumber);
      const e164Phone = parsedPhone?.format('E.164') || phoneNumber;

      await onEnroll(e164Phone, phoneType);

      // Clear form after successful enrollment
      setPhoneNumber('');
      setValidationError('');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleCancel = () => {
    setPhoneNumber('');
    setValidationError('');
    onClose();
  };

  const handlePhoneChange = (phone: string) => {
    setPhoneNumber(phone);
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add Phone Authentication Method</DialogTitle>
          <DialogContent className={styles.content}>
            <p>
              Add a phone number to receive verification codes via SMS or phone call
              for multi-factor authentication.
            </p>
            <Field
              label="Phone Number"
              required
              validationMessage={validationError}
              validationState={validationError ? 'error' : 'none'}
            >
              <div className={styles.phoneInput}>
                <PhoneInput
                  defaultCountry="us"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  disabled={isEnrolling}
                  placeholder="+1 (202) 555-1234"
                />
              </div>
            </Field>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={handleCancel}
              disabled={isEnrolling}
            >
              Cancel
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleSubmit}
              disabled={isEnrolling}
              loading={isEnrolling}
              loadingText="Adding..."
            >
              Add
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
